import { Heartbeat, Warning, CheckCircle, X } from "@phosphor-icons/react";
import { useState, useRef } from "react";

/**
 * Tenant Health Score visual primitives.
 *
 *   <HealthDot health={t.health} />              // small colored dot
 *   <HealthBadge health={t.health} />            // pill with score + label
 *   <HealthScoreChip health={t.health} />        // numeric chip with hover tooltip
 *   <HealthDetailCard health={t.health} />       // full card for detail view
 */

const LEVEL_THEME = {
  healthy:   { dot: "bg-emerald-500",    text: "text-emerald-400",   border: "border-emerald-500/30", bg: "bg-emerald-500/10",   label: "Saudável", icon: CheckCircle },
  attention: { dot: "bg-amber-400",      text: "text-amber-400",     border: "border-amber-400/30",   bg: "bg-amber-400/10",     label: "Atenção",  icon: Warning },
  critical:  { dot: "bg-rose-500",       text: "text-rose-400",      border: "border-rose-500/30",    bg: "bg-rose-500/10",      label: "Crítico",  icon: Warning },
};

const SEVERITY_THEME = {
  critical: "text-rose-400",
  warning:  "text-amber-400",
  info:     "text-slate-400",
};

const FACTOR_LABELS = {
  whatsapp:  "WhatsApp",
  activity:  "Atividade recente",
  volume:    "Volume de uso",
  margin:    "Margem operacional",
  handoff:   "Taxa de handoffs",
  failures:  "Taxa de falhas",
  decisions: "Motor de Decisão",
  roi:       "ROI gerado",
};

export function HealthDot({ health, size = 8 }) {
  const t = LEVEL_THEME[health?.level] || LEVEL_THEME.attention;
  return (
    <span
      className={`inline-block rounded-full ${t.dot} ${health?.level === "critical" ? "animate-pulse" : ""}`}
      style={{ width: size, height: size }}
      data-testid={`health-dot-${health?.level}`}
    />
  );
}

export function HealthScoreChip({ health }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  if (!health) return <span className="text-slate-600 text-[12px]">—</span>;
  const t = LEVEL_THEME[health.level] || LEVEL_THEME.attention;

  return (
    <span
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      className="relative inline-flex items-center gap-1.5 cursor-help"
      data-testid="health-score-chip"
      data-level={health.level}
    >
      <HealthDot health={health} />
      <span className={`text-[12px] font-semibold tabular-nums ${t.text}`}>{health.score}</span>
      {open && (
        <HealthTooltip health={health} />
      )}
    </span>
  );
}

function HealthTooltip({ health }) {
  const t = LEVEL_THEME[health.level] || LEVEL_THEME.attention;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute z-50 left-0 top-full mt-2 w-72 bg-slate-900 border border-white/10 rounded-lg shadow-2xl p-3 text-left cursor-default"
      data-testid="health-tooltip"
    >
      <div className="flex items-center gap-2 pb-2 border-b border-white/5 mb-2">
        <HealthDot health={health} size={9} />
        <div className="flex-1">
          <div className="font-heading text-[14px] font-semibold text-white">{health.score}/100 · {t.label}</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mt-0.5">Tenant Health Score</div>
        </div>
      </div>
      {health.reasons?.length === 0 ? (
        <div className="text-[12px] text-emerald-400 flex items-center gap-1.5 py-1">
          <CheckCircle size={12} weight="regular" /> Tudo dentro do esperado
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {health.reasons.map((r, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[12px] leading-snug">
              <span className={`shrink-0 ${SEVERITY_THEME[r.severity] || "text-slate-400"}`}>•</span>
              <span className={SEVERITY_THEME[r.severity] || "text-slate-300"}>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] text-slate-600 mt-2 pt-2 border-t border-white/5">
        Clique no tenant para ver os detalhes
      </div>
    </div>
  );
}

export function CriticalAlertBar({ counts, total, onFilterCritical, currentFilter }) {
  if (!counts || counts.critical === 0) return null;
  const isFiltered = currentFilter === "critical";
  return (
    <div
      data-testid="critical-alert-bar"
      className="flex items-center justify-between bg-gradient-to-r from-rose-500/15 via-rose-500/10 to-transparent border border-rose-500/25 rounded-lg px-4 py-3 mb-4"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center animate-pulse">
          <Warning size={15} weight="fill" className="text-rose-400" />
        </div>
        <div>
          <div className="text-[13px] text-white font-semibold">
            {counts.critical} {counts.critical === 1 ? "tenant crítico" : "tenants críticos"} requerem atenção
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {counts.attention > 0 && (<>{counts.attention} em atenção · </>)}
            {counts.healthy} saudáveis · score abaixo de 50 = crítico
          </div>
        </div>
      </div>
      <button
        onClick={() => onFilterCritical?.(isFiltered ? null : "critical")}
        data-testid="critical-alert-filter-btn"
        className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-400 text-white px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors"
      >
        {isFiltered ? <><X size={12} weight="bold" /> Limpar filtro</> : "Ver apenas críticos"}
      </button>
    </div>
  );
}

export function HealthDetailCard({ health }) {
  if (!health) return null;
  const t = LEVEL_THEME[health.level] || LEVEL_THEME.attention;
  const Icon = t.icon;
  const factors = Object.entries(health.factors || {});
  const weights = health.weights || {};

  return (
    <div
      data-testid="health-detail-card"
      className={`relative bg-white/[0.03] border ${t.border} rounded-lg p-5 overflow-hidden`}
    >
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${t.dot}`} />

      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium mb-1">
            <Heartbeat size={11} weight="regular" /> Tenant Health Score
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`font-heading text-4xl font-semibold tracking-tight ${t.text} tabular-nums`}>
              {health.score}
            </span>
            <span className="text-[12px] text-slate-500">/100</span>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${t.bg} ${t.border} border`}>
          <Icon size={13} weight="fill" className={t.text} />
          <span className={`text-[12px] font-semibold ${t.text}`}>{t.label}</span>
        </div>
      </header>

      {/* Reasons */}
      {health.reasons?.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-2">
            Motivos
          </div>
          <ul className="space-y-1.5">
            {health.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[12px] leading-snug">
                <span className={`shrink-0 ${SEVERITY_THEME[r.severity] || "text-slate-400"}`}>•</span>
                <span className="text-slate-200">{r.label}</span>
                <span className={`text-[10px] uppercase tracking-wider ml-auto shrink-0 ${SEVERITY_THEME[r.severity]}`}>
                  {r.severity}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Factor breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-2">
          Composição do score
        </div>
        <div className="space-y-2">
          {factors.map(([key, value]) => {
            const w = weights[key] || 0;
            const earned = (value * w);
            const pct = Math.round(value * 100);
            const color = value >= 0.8 ? "bg-emerald-500" : value >= 0.5 ? "bg-amber-400" : "bg-rose-500";
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="text-[11px] text-slate-400 w-32 shrink-0">{FACTOR_LABELS[key] || key}</span>
                <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-[11px] text-slate-300 tabular-nums w-14 text-right shrink-0">
                  {earned.toFixed(1)}/{w}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
